import './App.css';
import LoginPage from "./LoginPage/LoginPage";
import ResetPasswordPage from "./LoginPage/ResetPasswordPage";
import { Navigate, Route, Routes, useParams, useLocation } from "react-router-dom";
import Navigation from "./Navigation/Navigation.tsx";
import React, { useEffect } from "react";
import BuildingPage from "./Pages/BuildingDetails/BuildingPage";
import MapOverview from "./MapOverview/MapOverview";
import EkonomiPage from "./Pages/Ekonomi/EkonomiPage";
import ProtectedRoute from "./ProtectedRoute";
import AdminProtectedRoute from "./AdminProtectedRoute";
import FeatureRoute from "./Components/FeatureRoute";
import ParkingPage from "./Pages/Parking/ParkingPage";
import { onAuthStateChanged } from "firebase/auth";
import NotFoundPage from "./Pages/NotFoundPage";
import ManagementPage from "./Pages/Förvaltning/ManagementPage";
import TimelinePage from "./Pages/Timeline/TimelinePage";
import ProjectBoardPage from "./Pages/Projects/ProjectBoardPage";
import ProjectPage from "./Pages/Projects/ProjectPage";
import AnalysisPage from "./Pages/Analysis/AnalysisPage";
import RoomPage from "./Pages/Room/RoomPage";
import DocumentsPage from "./Pages/Documents/DocumentsPage";
import ComponentPage from "./Pages/ComponentPage/ComponentPage";
import UnifiedBuildingImages from "./Pages/BuildingDetails/UnifiedBuildingImages";
import LevelPage from "./Pages/Level/LevelPage";
import GenericComponentListPage from "./Pages/BuildingDetails/GenericComponentListPage";
import { BaseType } from "./types/Enums";
import { BuildingProvider } from "./Helpers/BuildingContext";
import { auth } from "./firebase";
import { markCurrentUserActive } from "./api/usersActivityApi";
import PermitsPage from "./Pages/Permits/PermitsPage";

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import MaintenanceTemplatesEditor from "./Pages/Förvaltning/TemplatesEditor/MaintenanceTemplatesEditor";
import MaintenanceDraft from "./Pages/Förvaltning/MaintenanceDraft/MaintenanceDraft";
import ReportDraftPage from "./Pages/ReportDrafts/ReportDraftPage";
import MaintenanceStart from "./Pages/Förvaltning/MaintenanceStart/MaintenanceStart";
import MaintenanceActive from "./Pages/Förvaltning/MaintenanceActive/MaintenanceActive";
import ComponentValidation from "./Pages/Förvaltning/ComponentValidation/ComponentValidation";
import ConfigValidation from "./Pages/Förvaltning/ConfigValidation/ConfigValidation";
import SettingsPage from "./Pages/Settings/SettingsPage";
import BuildingChecksPage from "./Pages/BuildingChecks/BuildingChecksPage";
import AddBuildingCheckPage from "./Pages/BuildingChecks/AddBuildingCheckPage";
import EditBuildingCheckPage from "./Pages/BuildingChecks/EditBuildingCheckPage";
import AllBuildingChecksPage from "./Pages/BuildingChecks/AllBuildingChecksPage";
import ProfilePage from "./Pages/Profile/ProfilePage";
import ChangePasswordPage from "./Pages/Profile/ChangePasswordPage";
import MyTodosPage from "./Pages/Profile/MyTodosPage";
import PersonalNotesPage from "./Pages/Profile/PersonalNotesPage";
import BuildingHistoryPage from "./Pages/BuildingDetails/BuildingHistoryPage";
import { BuildingExternalContactsPage } from "./Pages/BuildingDetails/BuildingExternalContactsPage";
import ArchiveDashboard from "./Pages/Archive/ArchiveDashboard";
import SetupPage from "./Pages/Setup/SetupPage";
import SuperadminMenuPage from "./Pages/Superadmin/SuperadminMenuPage";
import InstanceSettingsPage from "./Pages/Superadmin/InstanceSettingsPage";
import ConfigExportPage from "./Pages/Superadmin/ConfigExportPage";
import BuildingExportPage from "./Pages/Superadmin/BuildingExportPage";
import BuildingImportPage from "./Pages/Superadmin/BuildingImportPage";
import InventoryTimesPage from "./Pages/Superadmin/InventoryTimes/InventoryTimesPage";
import UserActivityPage from "./Pages/Superadmin/UserActivity/UserActivityPage";
import MaintenanceLibraryPage from "./Pages/Settings/MaintenanceLibraryPage";
import LifetimeLibraryPage from "./Pages/Settings/LifetimeLibraryPage";
import InspectionLibraryPage from "./Pages/Settings/InspectionLibraryPage";
import DataMigrations from "./Pages/Settings/DataMigrations";
import { BuildComponentTypesProvider } from "./contexts/BuildComponentTypesContext";
import { MaterialsProvider } from "./contexts/MaterialsContext";
import { DocumentCategoriesProvider } from "./contexts/DocumentCategoriesContext";
import { OperatingCostCategoriesProvider } from "./contexts/OperatingCostCategoriesContext";
import TodosPage from "./Pages/Todos/TodosPage";
import RisksOverviewPage from "./Pages/BuildingDetails/RisksOverview/RisksOverviewPage";
import OperatingCostsPage from "./Pages/OperatingCosts/OperatingCostsPage";
import LeasesPage from "./Pages/Leases/LeasesPage";
import TenantsPage from "./Pages/Tenants/TenantsPage";
import ParkingAreasPage from './Pages/Parking/ParkingAreasPage';
import ParkingAreaPage from './Pages/Parking/ParkingAreaPage';
import StorageUnitsPage from './Pages/StorageUnits/StorageUnitsPage';
import UnitsPage from './Pages/Units/UnitsPage';
import KeyRegisterPage from './Pages/KeyRegister/KeyRegisterPage';
import { ToastProvider } from "./Components/ToastContext";
import GuestPortalPage from "./Pages/Guest/GuestPortalPage";
import GuestWorkOrderRoute from "./Pages/Guest/GuestWorkOrderRoute";
import GuestProjectRoute from "./Pages/Guest/GuestProjectRoute";
import GuestReportLinkPage from "./Pages/Guest/GuestReportLinkPage";
import GPSMapPage from "./Pages/BuildingDetails/GPSMapPage";
import PublicReportPage from "./Pages/PublicReport/PublicReportPage";
import ChangelogPage from "./Pages/Changelog/ChangelogPage";

function App() {

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                // Refresh the user's "last active" timestamp so web usage counts,
                // not just iOS app launches. Best-effort; never blocks the app.
                markCurrentUserActive();
            }
        });

        return () => unsubscribe();
    }, []);

    const RedirectToBuilding = () => {
        const { id } = useParams();
        return <Navigate to={`/building/${id}`} replace />;
    };

    // The takplan page was merged into the roof page (its "Takritning" view).
    const RedirectToRoof = () => {
        const { id } = useParams();
        return <Navigate to={`/building/${id}/roof`} replace />;
    };

    const location = useLocation();
    const isGuestRoute = location.pathname.startsWith('/guest/');
    const isPublicReportRoute = location.pathname.startsWith('/report/');
    const isResetPasswordRoute = location.pathname === '/reset-password';

    // Guest routes render without App-header wrapper for proper scrolling
    if (isGuestRoute) {
        return (
            <ToastProvider>
                <Routes>
                    {/* Personal guest link: the portal lists everything delegated to the user */}
                    <Route path="/guest/:token/wo/:workOrderKey" element={<GuestWorkOrderRoute />} />
                    <Route path="/guest/:token/project/:projectKey" element={<GuestProjectRoute />} />
                    <Route path="/guest/:token/rapportera" element={<GuestReportLinkPage />} />
                    <Route path="/guest/:token" element={<GuestPortalPage />} />
                </Routes>
            </ToastProvider>
        );
    }

    // Set-password page: outside the data providers, which swap their children for a
    // spinner once the page signs in. A remount would re-check the code it just used
    // and show the link as expired (molnfast-react#1783).
    if (isResetPasswordRoute) {
        return (
            <Routes>
                <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Routes>
        );
    }

    // Public report route - no authentication required
    if (isPublicReportRoute) {
        return (
            <ToastProvider>
                <Routes>
                    <Route path="/report/:token" element={<PublicReportPage />} />
                </Routes>
            </ToastProvider>
        );
    }

    return (
        <ToastProvider>
            <div className="App">
                <header className="App-header">
                    <MaterialsProvider>
                    <BuildComponentTypesProvider>
                        <DocumentCategoriesProvider>
                        <OperatingCostCategoriesProvider>
                        <BuildingProvider>
                            <Routes>

                                <Route path="/" element={<LoginPage />} />
                                <Route path="/home" element={<LoginPage />} />
                                <Route path="/signIn" element={<LoginPage />} />

                                <Route
                                    path="/"
                                    element={
                                        <ProtectedRoute>
                                            <Navigation />
                                        </ProtectedRoute>
                                    }
                                >
                                    <Route index element={<MapOverview />} />
                                    <Route path="map" element={<MapOverview />} />
                                    <Route path="economy" element={<EkonomiPage />} />
                                    <Route path="management" element={<ManagementPage />} />
                                    <Route path="timeline" element={<TimelinePage />} />
                                    <Route path="projects" element={<ProjectBoardPage />} />
                                    <Route path="analysis" element={<AnalysisPage />} />
                                    <Route path="tenants" element={
                                        <AdminProtectedRoute>
                                            <FeatureRoute feature="tenants"><TenantsPage /></FeatureRoute>
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="/leases" element={
                                        <AdminProtectedRoute>
                                            <FeatureRoute feature="tenants"><LeasesPage /></FeatureRoute>
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="building/:id" element={<BuildingPage />} />
                                    <Route path="/building/:id/outdoor/:category" element={<GenericComponentListPage type={BaseType.Outdoor} />} />
                                    <Route path="/building/:id/roof" element={<GenericComponentListPage type={BaseType.Roof} />} />
                                    <Route path="/building/:id/facade" element={<GenericComponentListPage type={BaseType.Facade} />} />
                                    <Route path="/building/:id/balcony" element={<GenericComponentListPage type={BaseType.Balcony} />} />
                                    <Route path="/building/:id/foundation" element={<GenericComponentListPage type={BaseType.Foundation} />} />
                                    <Route path="/building/:id/chimney" element={<GenericComponentListPage type={BaseType.Chimney} />} />
                                    <Route path="/building/:id/roof_drainage" element={<GenericComponentListPage type={BaseType.RoofDrainage} />} />
                                    <Route path="/building/:id/ground_drainage" element={<GenericComponentListPage type={BaseType.GroundDrainage} />} />
                                    <Route path="/building/:id/riser" element={<GenericComponentListPage type={BaseType.Riser} />} />
                                    <Route path="/building/:id/miscellaneous" element={<GenericComponentListPage type={BaseType.Miscellaneous} />} />
                                    <Route path="/building/:id/fixed_furnishing" element={<GenericComponentListPage type={BaseType.FixedFurnishing} />} />
                                    <Route path="/building/:id/windows" element={<GenericComponentListPage type={BaseType.Window} />} />
                                    <Route path="/building/:id/doors" element={<GenericComponentListPage type={BaseType.Door} />} />
                                    <Route path="/building/:id/sheet-metal" element={<GenericComponentListPage type={BaseType.SheetMetal} />} />
                                    <Route path="/building/:id/utilities/:category" element={<GenericComponentListPage type={BaseType.Utility} />} />
                                    <Route path="/building/:id/documents" element={
                                        <DocumentsPage title="Dokument & anteckningar" />
                                    } />
                                    <Route path="/building/:id/external-contacts" element={<BuildingExternalContactsPage />} />
                                    <Route path="/building/:id/room/:roomId" element={<RoomPage />} />
                                    <Route path="/building/:id/room" element={<RedirectToBuilding />} />
                                    <Route path="/building/:id/level/:levelId" element={<LevelPage />} />
                                    <Route path="/building/:id/level" element={<RedirectToBuilding />} />
                                    <Route path="/building/:id/roofplan" element={<RedirectToRoof />} />
                                    <Route path="/building/:buildingKey/component/:componentKey/history" element={<BuildingHistoryPage />} />
                                    <Route path="/building/:buildingKey/level/:levelKey/history" element={<BuildingHistoryPage />} />
                                    <Route path="/building/:buildingKey/room/:roomKey/history" element={<BuildingHistoryPage />} />
                                    <Route path="/building/:buildingKey/component/:itemId" element={<ComponentPage />} />
                                    <Route path="/building/:id/images" element={<UnifiedBuildingImages />} />
                                    <Route path="/building/:id/permits" element={<PermitsPage />} />
                                    <Route path="/building/:id/checks" element={<BuildingChecksPage />} />
                                    <Route path="/building/:id/checks/add" element={<AddBuildingCheckPage />} />
                                    <Route path="/building/:id/checks/:checkKey/edit" element={<EditBuildingCheckPage />} />
                                    <Route path="/building/:id/roof-safety" element={<GenericComponentListPage type={BaseType.RoofSafety} />} />
                                    <Route path="/building/:id/risks" element={<RisksOverviewPage />} />
                                    <Route path="/building/:id/history" element={<BuildingHistoryPage />} />
                                    <Route path="/building/:id/todos" element={<TodosPage />} />
                                    <Route path="/building/:id/operating-costs" element={<OperatingCostsPage />} />
                                    <Route path="/building/:id/parking" element={<FeatureRoute feature="parking"><ParkingAreasPage /></FeatureRoute>} />
                                    <Route path="/building/:id/parking/:areaKey" element={<FeatureRoute feature="parking"><ParkingAreaPage /></FeatureRoute>} />
                                    <Route path="/building/:id/storage-units" element={<FeatureRoute feature="storageUnits"><StorageUnitsPage /></FeatureRoute>} />
                                    <Route path="/building/:id/units" element={<FeatureRoute feature="units"><UnitsPage /></FeatureRoute>} />
                                    <Route path="/building/:buildingKey/timeline" element={<TimelinePage />} />
                                    <Route path="/building/:id/gps-map" element={<GPSMapPage />} />
                                    <Route path="/building/:id/projects" element={<ProjectBoardPage />} />
                                    <Route path="/building/:id/projects/:projectKey" element={<ProjectPage />} />
                                    <Route path="/building-checks" element={<AllBuildingChecksPage />} />
                                    <Route path="/maintenance" element={<MaintenanceStart />} />
                                    <Route path="/maintenanceTemplates" element={<MaintenanceTemplatesEditor />} />
                                    <Route path="/building/:buildingKey/validate-maintenance-components" element={<ComponentValidation />} />
                                    <Route path="/building/:buildingKey/validate-maintenance-config" element={<ConfigValidation />} />
                                    <Route path="/maintenanceDrafts/:buildingKey" element={<MaintenanceDraft />} />
                                    <Route path="/maintenanceActive/:buildingKey" element={<MaintenanceActive />} />
                                    <Route path="/report-drafts/:draftKey" element={
                                        <AdminProtectedRoute>
                                            <ReportDraftPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="/parking" element={
                                        <AdminProtectedRoute>
                                            <FeatureRoute feature="parking"><ParkingPage /></FeatureRoute>
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="/parking-queue" element={
                                        <AdminProtectedRoute>
                                            <FeatureRoute feature="parking"><ParkingPage /></FeatureRoute>
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="/keys" element={
                                        <AdminProtectedRoute>
                                            <FeatureRoute feature="keyRegister"><KeyRegisterPage /></FeatureRoute>
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="settings/*" element={
                                        <AdminProtectedRoute>
                                            <SettingsPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="archive" element={
                                        <AdminProtectedRoute>
                                            <ArchiveDashboard />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="setup" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <SetupPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <SuperadminMenuPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/activity" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <UserActivityPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/inventory-times" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <InventoryTimesPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/maintenance-library" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <MaintenanceLibraryPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/lifetime-library" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <LifetimeLibraryPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/inspection-library" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <InspectionLibraryPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/data-migrations" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <DataMigrations />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/config-export" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <ConfigExportPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/building-export" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <BuildingExportPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/building-import" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <BuildingImportPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="superadmin/instance-settings" element={
                                        <AdminProtectedRoute requireSuperadmin>
                                            <InstanceSettingsPage />
                                        </AdminProtectedRoute>
                                    } />
                                    <Route path="profile" element={<ProfilePage />} />
                                    <Route path="versionshistorik" element={<ChangelogPage />} />
                                    <Route path="change-password" element={<ChangePasswordPage />} />
                                    <Route path="my-todos" element={<MyTodosPage />} />
                                    <Route path="my-notes" element={<PersonalNotesPage />} />
                                    <Route path="*" element={<NotFoundPage />} />

                                </Route>

                            </Routes>
                        </BuildingProvider>
                        </OperatingCostCategoriesProvider>
                        </DocumentCategoriesProvider>
                    </BuildComponentTypesProvider>
                    </MaterialsProvider>
                </header>
            </div>
        </ToastProvider>
    );
}

export default App;
